import React, { useState, useEffect, useMemo, useRef } from "react";
import { X, Zap } from "lucide-react";
import {
  parseIntakeTimeForDisplay,
  formatTimeForSave,
} from "../../../core/diet-plan/intakeTime";
import {
  scaleFoodForQuantityEdit,
  formatCompactNutrition,
} from "../../../core/nutrients/calculateMealNutrients";
import { getRecipeDetails } from "../../../core/diet-plan/recipeId";
import IntakeTimePicker from "../IntakeTimePicker";

// Calories + Protein/Carbs/Fats/Fiber/Sugar summary. Shared between this
// modal's live preview and FoodItemCard's own display, so both stay visually
// identical (FoodItemCard already depends on this file to render the modal,
// so defining it here — rather than the other way round — avoids a module
// cycle between the two files).
export const NutritionSummaryCard = ({ nutrition }) => (
  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-3 border border-gray-200">
    <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-300">
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-orange-500" />
        <span className="text-xs font-medium text-gray-600">Calories</span>
      </div>
      <span className="text-lg font-bold text-gray-900">
        {nutrition.calories} <span className="text-sm font-normal text-gray-600">kcal</span>
      </span>
    </div>
    <div className="grid grid-cols-5 gap-1.5">
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-gray-600">Protein</span>
        <span className="text-xs font-semibold text-gray-800">{nutrition.protein}g</span>
      </div>
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-gray-600">Carbs</span>
        <span className="text-xs font-semibold text-gray-800">{nutrition.carbs}g</span>
      </div>
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-gray-600">Fats</span>
        <span className="text-xs font-semibold text-gray-800">{nutrition.fats}g</span>
      </div>
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-gray-600">Fiber</span>
        <span className="text-xs font-semibold text-gray-800">{nutrition.fiber}g</span>
      </div>
      <div className="flex flex-col items-center gap-1">
        <span className="text-xs text-gray-600">Sugar</span>
        <span className="text-xs font-semibold text-gray-800">{nutrition.sugar}g</span>
      </div>
    </div>
  </div>
);

const FoodItemActionsModal = ({
  isOpen,
  onClose,
  food,
  onSave,
  availableUnits = [],
  // The meal-slot card owns timing (the meal intake window); hide the per-recipe time.
  intakeTimeReadOnly = false,
}) => {
  const [quantity, setQuantity] = useState(food?.quantity || 1);
  const [unit, setUnit] = useState(food?.quantity_type || "g");
  const [timeValue, setTimeValue] = useState(
    food ? parseIntakeTimeForDisplay(food.intake_time || food.time || "") : ""
  );
  const quantityInputRef = useRef(null);

  // Live macro preview: recomputed with the exact same scaling used when the
  // edit is actually saved (DietMealSlotEditor.handleUpdateRecipeItem), so
  // this can never drift from the value that gets persisted.
  const detail = getRecipeDetails(food);
  const parsedQuantity = parseFloat(quantity) || 0;
  const liveNutrition = useMemo(() => {
    if (!food) return null;
    return formatCompactNutrition(
      scaleFoodForQuantityEdit(food, detail, parsedQuantity, unit)
    );
  }, [food, detail, parsedQuantity, unit]);

  useEffect(() => {
    if (isOpen && food) {
      setQuantity(food.quantity || 1);
      setUnit(food.quantity_type || "g");
      setTimeValue(parseIntakeTimeForDisplay(food.intake_time || food.time || ""));
      // Focus quantity input when modal opens
      setTimeout(() => {
        if (quantityInputRef.current) {
          quantityInputRef.current.focus();
          quantityInputRef.current.select();
        }
      }, 100);
    }
  }, [isOpen, food]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (!quantity || isNaN(quantity) || parseFloat(quantity) <= 0) {
      return;
    }

    let newQuantityTypeId = food.quantity_type_id;

    if (unit !== food.quantity_type && food.food_details?.measures) {
      if (food.unitToIdMap && food.unitToIdMap[unit]) {
        newQuantityTypeId = food.unitToIdMap[unit];
      }
    }

    const formattedTime = intakeTimeReadOnly
      ? food.intake_time ?? null
      : timeValue
        ? formatTimeForSave(timeValue)
        : null;

    const updatedFood = {
      ...food,
      quantity: parseFloat(quantity),
      quantity_type: unit,
      quantity_type_id: newQuantityTypeId,
      intake_time: formattedTime,
    };

    onSave(updatedFood);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">
            {food?.food_name ||
              food?.food_details?.main_details?.food_name ||
              "Food Item"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity
            </label>
            <div className="flex gap-2">
              <input
                ref={quantityInputRef}
                type="number"
                value={quantity}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    setQuantity(value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSave();
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary"
                placeholder="Quantity"
                step="0.1"
                min="0"
              />
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-primary bg-white font-medium"
              >
                {availableUnits.length > 0 ? (
                  availableUnits.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))
                ) : (
                  <option value={unit}>{unit}</option>
                )}
              </select>
            </div>
          </div>

          {liveNutrition && <NutritionSummaryCard nutrition={liveNutrition} />}

          {!intakeTimeReadOnly && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Intake Time
              </label>
              <div>
                <IntakeTimePicker value={timeValue} onChange={setTimeValue} />
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!quantity || isNaN(quantity) || parseFloat(quantity) <= 0}
              className="flex-1 px-4 py-2 bg-orange-primary hover:bg-orange-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodItemActionsModal;
